import { useState, useRef, useEffect, useCallback } from "react";
import CAS1 from "./components/sections/CAS1";
import CBS1 from "./components/sections/CBS1";
import CAS2 from "./components/sections/CAS2";
import CAS3 from "./components/sections/CAS3";
import CAS4 from "./components/sections/CAS4";
import CBS2 from "./components/sections/CBS2";
import CBS3 from "./components/sections/CBS3";
import SplashPage from "./components/sections/SplashPages";
import Landing from "./pages/Landing";
import Welcome from "./pages/Welcome";
import Chatbot, {
  CONVERSATION_SCRIPTS,
  type ChatMessage,
  type QuickReply,
  type SectionKey,
} from "./components/shared/Chatbot";
import {
  getProfileByToken,
  markSessionComplete,
  updateProfileStatus,
  saveSurveyResponse,
  savePrebooking,
  saveInvestorExpression,
  saveNewsletterOptIn,
  saveAppMemberFromPrebooking,
  saveAppMemberFromInvestor,
  type Profile,
} from "./lib/supabase";
import "./components/sections/CBS1.css";
import "./App.css";

// ─── Types ───────────────────────────────────────────────

interface UnlockState {
  CAS1: boolean;
  CAS2: boolean;
  CAS3: boolean;
  CAS4: boolean;
  CBS1: boolean;
  CBS2: boolean;
  CBS3: boolean;
}

interface UserData {
  surveyResponses?: {
    wellbeingCare: number;
    resonatesWithConcept: number;
    cityAmenityGap: number;
    ageRange: string;
  };
  prebooking?: {
    package: "single" | "five-pack";
    amount: number;
  };
  investmentInterest?: {
    amount: number;
    accredited: boolean;
  };
}

type CBSection = "CBS1" | "CBS2" | "CBS3" | null;
type AppState =
  | "loading"
  | "landing"
  | "welcome"
  | "invalid"
  | "complete"
  | "ready";

const PROMPT_TEXT: Record<string, string> = {
  CAS1: "Like what you see? 👀",
  CAS1_LANDLORD: "Like what you see? 👀",
  CBS1: "Thanks for that! 🙏",
  CAS2: "Heavy stuff, right? 😓",
  CAS2_LANDLORD: "Now you know why it works 💡",
  CBS2: "So... what do you think? 💭",
  CAS3: "So... what do you think? 💭",
  CAS4: "Quick question for you 💬",
};

// ─── App ─────────────────────────────────────────────────

function App() {
  // ── All state ──────────────────────────────────────────
  const [appState, setAppState] = useState<AppState>("loading");
  const [profile, setProfile] = useState<Profile | null>(null);
  const [unlocked, setUnlocked] = useState<UnlockState>({
    CAS1: true,
    CAS2: false,
    CAS3: false,
    CAS4: false,
    CBS1: true,
    CBS2: false,
    CBS3: false,
  });
  const [currentLane, setCurrentLane] = useState<"CA" | "CB">("CA");
  const [currentCBSection, setCurrentCBSection] = useState<CBSection>(null);
  const [, setUserData] = useState<UserData>({});
  const [showSplash, setShowSplash] = useState<string | null>(null);
  const [chatIsOpen, setChatIsOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatReplies, setChatReplies] = useState<QuickReply[]>([]);
  const [chatStep, setChatStep] = useState(0);
  const [chatSection, setChatSection] = useState<SectionKey>("CAS1");
  const [chatShowNotification, setChatShowNotification] = useState(true);
  const [chatShowPrompt, setChatShowPrompt] = useState(false);
  const [chatConversationComplete, setChatConversationComplete] =
    useState(false);

  // ── All refs ───────────────────────────────────────────
  const caScrollPosition = useRef(0);
  const caLaneRef = useRef<HTMLDivElement>(null);

  // ── Token validation ───────────────────────────────────
  useEffect(() => {
    async function validateToken() {
      const params = new URLSearchParams(window.location.search);

      const page = params.get("page");
      if (page === "welcome") {
        setAppState("welcome");
        return;
      }

      const token = params.get("token");
      if (!token) {
        setAppState("landing");
        return;
      }

      const fetchedProfile = await getProfileByToken(token);

      if (!fetchedProfile) {
        setAppState("invalid");
        return;
      }

      if (fetchedProfile.session_complete && !fetchedProfile.is_admin) {
        setAppState("complete");
        return;
      }

      setProfile(fetchedProfile);

      if (fetchedProfile.status === "pending") {
        updateProfileStatus(fetchedProfile.id, "in_progress");
      }

      if (fetchedProfile.is_admin) {
        setUnlocked({
          CAS1: true,
          CAS2: true,
          CAS3: true,
          CAS4: true,
          CBS1: true,
          CBS2: true,
          CBS3: true,
        });
      } else if (fetchedProfile.user_type === "landlord") {
        setUnlocked((prev) => ({
          ...prev,
          CAS2: true,
          CAS4: true,
          CBS1: false,
        }));
        setChatSection("CAS1_LANDLORD");
      }

      setAppState("ready");
    }

    validateToken();
  }, []);

  // ── Lane scroll save ───────────────────────────────────
  useEffect(() => {
    if (currentLane === "CB" && caLaneRef.current) {
      caScrollPosition.current = caLaneRef.current.scrollTop;
    }
  }, [currentLane]);

  // ── All callbacks ──────────────────────────────────────
  const handleChatbotTrigger = useCallback(
    (section: SectionKey, visible: boolean) => {
      if (visible && !chatIsOpen && !chatConversationComplete) {
        setChatSection(() => {
          // Landlord remapping — use profile type, not prev state
          if (profile?.user_type === "landlord") {
            if (section === "CAS1") return "CAS1_LANDLORD";
            if (section === "CAS2") return "CAS2_LANDLORD";
          }
          return section;
        });
        setChatShowPrompt(true);
        setChatShowNotification(false);
      }
      if (!visible && !chatIsOpen) setChatShowPrompt(false);
    },
    [chatIsOpen, chatConversationComplete, profile?.user_type]
  );

  const handleCBS1ChatbotTrigger = useCallback(() => {
    setChatSection("CAS1");
    setChatConversationComplete(false);
    setChatIsOpen(true);
    setChatShowPrompt(false);
    setChatShowNotification(false);
    const script = CONVERSATION_SCRIPTS["CAS1"];
    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          text: script[2].bot,
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
      setChatReplies(script[2].replies);
      setChatStep(2);
    }, 500);
  }, []);

  const advanceChatbotToSection = useCallback((section: SectionKey) => {
    setChatSection(section);
    setChatConversationComplete(false);
    setChatShowPrompt(false);
    setChatShowNotification(false);
  }, []);

  // ── Helper functions (not hooks) ───────────────────────
  const addBotMessage = (text: string) => {
    setChatMessages((prev) => [
      ...prev,
      {
        id: `bot-${Date.now()}-${Math.random()}`,
        text,
        sender: "bot",
        timestamp: new Date(),
      },
    ]);
  };

  const addUserMessage = (text: string) => {
    setChatMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}-${Math.random()}`,
        text,
        sender: "user",
        timestamp: new Date(),
      },
    ]);
  };

  const exitTo = (
    splash: string,
    stage:
      | "CAS1"
      | "CAS1_LANDLORD"
      | "CBS1"
      | "CAS2"
      | "CBS2"
      | "CAS3"
      | "CBS3"
      | "CAS4",
    exitDetail?: string
  ) => {
    if (profile && !profile.is_admin) {
      updateProfileStatus(profile.id, "complete", stage, exitDetail);
      markSessionComplete(profile.id);
    }
    setShowSplash(splash);
  };

  const navigateToCB = (section: CBSection) => {
    if (section && unlocked[section]) {
      setChatIsOpen(false);
      setCurrentLane("CB");
      setCurrentCBSection(section);
    }
  };

  const navigateToCA = () => {
    setCurrentLane("CA");
    setTimeout(() => {
      if (caLaneRef.current)
        caLaneRef.current.scrollTop = caScrollPosition.current;
    }, 100);
  };

  // ── NOW safe to conditionally return ──────────────────

  if (appState === "landing") {
    return <Landing />;
  }

  if (appState === "loading") {
    return (
      <div
        style={{
          minHeight: "100dvh",
          background: "#0a0a0a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        <div
          style={{
            fontSize: "1.1rem",
            fontWeight: 600,
            letterSpacing: "0.5em",
            color: "#E8544F",
          }}
        >
          L O A
        </div>
      </div>
    );
  }
  if (appState === "welcome") {
    return <Welcome />;
  }
  if (appState === "invalid") {
    return (
      <div
        style={{
          minHeight: "100dvh",
          background: "#0a0a0a",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Outfit', sans-serif",
          padding: "40px",
          textAlign: "center",
          gap: "24px",
        }}
      >
        <div
          style={{
            fontSize: "1.1rem",
            fontWeight: 600,
            letterSpacing: "0.5em",
            color: "#E8544F",
          }}
        >
          L O A
        </div>
        <p
          style={{
            color: "rgba(250,248,243,0.4)",
            fontSize: "0.9rem",
            maxWidth: "280px",
            lineHeight: 1.7,
          }}
        >
          This link isn't valid. If you believe this is an error, reach out to
          us directly.
        </p>
      </div>
    );
  }

  if (appState === "complete") {
    return (
      <div
        style={{
          minHeight: "100dvh",
          background: "#0a0a0a",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Outfit', sans-serif",
          padding: "40px",
          textAlign: "center",
          gap: "24px",
        }}
      >
        <div
          style={{
            fontSize: "1.1rem",
            fontWeight: 600,
            letterSpacing: "0.5em",
            color: "#E8544F",
          }}
        >
          L O A
        </div>
        <p
          style={{
            color: "rgba(250,248,243,0.4)",
            fontSize: "0.9rem",
            maxWidth: "300px",
            lineHeight: 1.7,
          }}
        >
          You've already viewed this. Reach out to continue the conversation.
        </p>
      </div>
    );
  }

  // ── Chatbot handlers ───────────────────────────────────

  const handleChatOpen = () => {
    setChatIsOpen(true);
    setChatShowNotification(false);
    setChatShowPrompt(false);
    if (chatMessages.length === 0) {
      const script = CONVERSATION_SCRIPTS[chatSection];
      setTimeout(() => {
        addBotMessage(script[0].bot);
        setChatReplies(script[0].replies);
        setChatStep(0);
      }, 500);
    }
  };

  const handleChatPromptClick = () => {
    setChatIsOpen(true);
    setChatShowNotification(false);
    setChatShowPrompt(false);
    const script = CONVERSATION_SCRIPTS[chatSection];
    // CAS1 and CAS1_LANDLORD both jump to step 2 (the bridge message)
    // All other scripts start at step 0
    const targetStep =
      chatSection === "CAS1" || chatSection === "CAS1_LANDLORD" ? 2 : 0;
    setChatStep(targetStep);
    setTimeout(() => {
      addBotMessage(script[targetStep].bot);
      setChatReplies(script[targetStep].replies);
    }, 500);
  };

  const handleChatClose = () => setChatIsOpen(false);

  const handleReplyClick = (reply: QuickReply) => {
    addUserMessage(reply.text);
    setChatReplies([]);
    const script = CONVERSATION_SCRIPTS[chatSection];

    if (reply.action === "primary") {
      setChatConversationComplete(true);
      setTimeout(
        () => addBotMessage("Perfect! Let's make this happen 🚀"),
        500
      );
      setTimeout(() => {
        if (chatSection === "CAS1") {
          navigateToCB("CBS1");
        } else if (chatSection === "CAS1_LANDLORD") {
          // Landlord CAS1 primary shouldn't happen (no primary in script)
          // but defensive: scroll to CAS2
          setChatIsOpen(false);
          navigateToCA();
          setTimeout(() => {
            document
              .getElementById("cas2-section")
              ?.scrollIntoView({ behavior: "smooth" });
          }, 300);
        } else if (chatSection === "CAS2") {
          navigateToCB("CBS2");
        } else if (chatSection === "CBS2") {
          setChatIsOpen(false);
          advanceChatbotToSection("CAS3");
          navigateToCA();
          setTimeout(() => {
            document
              .getElementById("cas3-section")
              ?.scrollIntoView({ behavior: "smooth" });
          }, 300);
        } else if (chatSection === "CAS3") {
          navigateToCB("CBS3");
        } else if (chatSection === "CAS4") {
          window.location.href =
            "mailto:alex@tcglventures.com?subject=LOA%20%E2%80%94%20Location%20Partnership%20Enquiry";
          if (profile && !profile.is_admin) {
            updateProfileStatus(profile.id, "complete", "CAS4");
            markSessionComplete(profile.id);
          }
          setTimeout(() => setShowSplash("SPLASH_LANDLORD"), 800);
        }
      }, 1500);
    } else if (reply.action === "secondary") {
      setChatConversationComplete(true);
      setTimeout(
        () => addBotMessage("No worries! Thanks for checking it out 👋"),
        500
      );
      setTimeout(() => {
        if (chatSection === "CAS1" || chatSection === "CAS1_LANDLORD") {
          if (chatSection === "CAS1_LANDLORD") {
            // Landlord exits from CAS1
            if (profile && !profile.is_admin) {
              updateProfileStatus(profile.id, "complete", "CAS1_LANDLORD");
              markSessionComplete(profile.id);
            }
            setShowSplash("SPLASH_LANDLORD");
          } else if (chatStep <= 2) {
            exitTo("SPLASH_1", "CAS1", "exit_1.1"); // "Not really interested"
          } else if (chatStep === 3) {
            exitTo("SPLASH_1", "CBS1", "exit_1.2"); // "I'm out, thanks" (pre-survey)
          } else {
            exitTo("SPLASH_1", "CAS1", "exit_1.1");
          }
        } else if (chatSection === "CAS2") {
          exitTo("SPLASH_3", "CAS2", "exit_3.1"); // "Not ready for that yet"
        } else if (chatSection === "CBS2") {
          exitTo("SPLASH_COMMITTED", "CBS2", "exit_4.1"); // "I'm good for now"
        } else if (chatSection === "CAS3") {
          exitTo("SPLASH_COMMITTED", "CAS3", "exit_4.2"); // "Thanks, just the session"
        } else if (chatSection === "CAS4") {
          if (profile && !profile.is_admin) {
            updateProfileStatus(profile.id, "complete", "CAS4");
            markSessionComplete(profile.id);
          }
          setShowSplash("SPLASH_LANDLORD");
        }
      }, 1500);
    } else if (reply.action === "continue") {
      const nextStep = chatStep + 1;
      if (nextStep < script.length) {
        // Auto-close after step 1 for both CAS1 and CAS1_LANDLORD
        if (
          (chatSection === "CAS1" || chatSection === "CAS1_LANDLORD") &&
          nextStep === 1
        ) {
          setTimeout(() => {
            addBotMessage(script[1].bot);
            setChatReplies([]);
            setChatStep(1);
          }, 800);
          setTimeout(() => setChatIsOpen(false), 2500);
          return;
        }

        // CBS2 step 0 → step 1: casual users exit here, investors continue
        if (chatSection === "CBS2" && nextStep === 1) {
          if (
            profile?.user_type === "casual" ||
            (!profile?.is_admin &&
              profile?.user_type !== "investor" &&
              profile?.user_type !== "landlord")
          ) {
            // Casual: session ends after celebration
            setChatConversationComplete(true);
            setTimeout(() => {
              addBotMessage(
                "You're officially one of our founding members. We'll be in touch as soon as bookings open. See you on the inside! 🚀"
              );
            }, 800);
            setTimeout(() => {
              exitTo("SPLASH_COMMITTED", "CBS2", "exit_4.1");
            }, 2500);
            return;
          }
        }

        // CAS1 skip from step 3 to step 4 if CBS1 already done
        const targetStep =
          chatSection === "CAS1" &&
          nextStep === 3 &&
          currentCBSection === "CBS1"
            ? 4
            : nextStep;
        setTimeout(() => {
          addBotMessage(script[targetStep].bot);
          setChatReplies(script[targetStep].replies);
          setChatStep(targetStep);
        }, 800);
      }
    } else if (reply.action === "gateway_yes") {
      setChatConversationComplete(true);
      setUnlocked((prev) => ({ ...prev, CAS2: true, CBS2: true }));
      setTimeout(
        () =>
          addBotMessage("Brilliant! Let's show you the science behind it 🔬"),
        500
      );
      setTimeout(() => {
        setChatIsOpen(false);
        advanceChatbotToSection("CAS2");
        navigateToCA();
        setTimeout(() => {
          document
            .getElementById("cas2-section")
            ?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }, 1500);
    } else if (reply.action === "gateway_no") {
      setChatConversationComplete(true);
      setTimeout(
        () =>
          addBotMessage(
            "No worries at all. Thanks for taking the time — we really appreciate it 🙏"
          ),
        500
      );
      setTimeout(() => exitTo("SPLASH_2", "CBS1", "exit_2.1"), 2000);
    } else if (reply.action === "landlord_to_cas2") {
      setChatConversationComplete(true);
      setTimeout(
        () =>
          addBotMessage(
            "Great — scroll down through the evidence and I'll pick you up at the business case. 👇"
          ),
        500
      );
      setTimeout(() => {
        setChatIsOpen(false);
        advanceChatbotToSection("CAS2_LANDLORD");
        navigateToCA();
        setTimeout(() => {
          document
            .getElementById("cas2-section")
            ?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }, 1500);
    } else if (reply.action === "landlord_to_cas4") {
      setChatConversationComplete(true);
      setTimeout(
        () =>
          addBotMessage(
            "Let's get into it. Scroll down to the business case. 👇"
          ),
        500
      );
      setTimeout(() => {
        setChatIsOpen(false);
        advanceChatbotToSection("CAS4");
        navigateToCA();
        setTimeout(() => {
          document
            .getElementById("cas4-section")
            ?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }, 1500);
    }
  };

  // ── Section completion handlers ────────────────────────

  const handleCBS1Complete = (surveyData: any, _qualified: boolean) => {
    setUserData((prev) => ({
      ...prev,
      surveyResponses: {
        wellbeingCare: surveyData.wellbeingCare,
        resonatesWithConcept: surveyData.resonance,
        cityAmenityGap: surveyData.amenityGap,
        ageRange: surveyData.ageBracket,
      },
    }));
    if (profile) {
      saveSurveyResponse({
        wellbeing_care: surveyData.wellbeingCare,
        resonance: surveyData.resonance,
        amenity_gap: surveyData.amenityGap,
        gender: surveyData.gender,
        age_bracket: surveyData.ageBracket,
        preferred_experience: surveyData.preferredExperience,
        concerns: surveyData.concerns || undefined,
        ideas: surveyData.ideas || undefined,
        qualified: true,
      });
    }
  };

  const handleCBS2Complete = (
    packageType: "single" | "five-pack",
    amount: number
  ) => {
    setUserData((prev) => ({
      ...prev,
      prebooking: { package: packageType, amount },
    }));
    if (profile) {
      savePrebooking(profile.id, { package_type: packageType, amount });
      saveAppMemberFromPrebooking(profile, packageType, amount);
      updateProfileStatus(profile.id, "app_member", "CBS2");
    }
    if (profile?.user_type === "investor" || profile?.is_admin) {
      setUnlocked((prev) => ({ ...prev, CAS3: true, CBS3: true }));
    }
    setChatSection("CBS2");
    setChatConversationComplete(false);
    setChatIsOpen(true);
    setChatShowPrompt(false);
    setChatShowNotification(false);
    const script = CONVERSATION_SCRIPTS["CBS2"];
    setTimeout(() => {
      addBotMessage(script[0].bot);
      setChatReplies(script[0].replies);
      setChatStep(0);
    }, 500);
  };

  const handleCBS3Complete = (
    investmentData: UserData["investmentInterest"]
  ) => {
    setUserData((prev) => ({ ...prev, investmentInterest: investmentData }));
    if (profile && investmentData) {
      saveInvestorExpression(profile.id, {
        investment_tier: String(investmentData.amount),
        custom_amount: investmentData.amount || undefined,
        accredited: investmentData.accredited,
      });
      saveAppMemberFromInvestor(
        profile,
        String(investmentData.amount),
        investmentData.amount
      );
      updateProfileStatus(profile.id, "app_member", "CBS3", "exit_4.4");
      if (!profile.is_admin) markSessionComplete(profile.id);
    }
    setShowSplash("SPLASH_COMMITTED");
  };

  // ── Splash ─────────────────────────────────────────────

  if (showSplash) {
    return (
      <SplashPage
        type={
          showSplash as
            | "SPLASH_1"
            | "SPLASH_2"
            | "SPLASH_3"
            | "SPLASH_4"
            | "SPLASH_COMMITTED"
            | "SPLASH_LANDLORD"
        }
        onNewsletterOptIn={() => {
          if (profile) {
            saveNewsletterOptIn(profile.id, showSplash);
            updateProfileStatus(profile.id, "newsletter_only");
          }
        }}
      />
    );
  }

  // ── Render ─────────────────────────────────────────────

  return (
    <div className={`app-container ${currentLane === "CB" ? "show-cb" : ""}`}>
      <div className="progress-indicator">
        <div className="progress-dots">
          <span className={unlocked.CAS1 ? "active" : ""} />
          <span className={unlocked.CAS2 ? "active" : ""} />
          <span className={unlocked.CAS3 || unlocked.CAS4 ? "active" : ""} />
        </div>
      </div>

      <div className="lane-indicator">
        <button
          className={currentLane === "CA" ? "active" : ""}
          onClick={navigateToCA}
          disabled={currentLane === "CA"}
        >
          ← Showcase
        </button>
        <button
          className={currentLane === "CB" ? "active" : ""}
          disabled={currentLane === "CB" || !currentCBSection}
        >
          Action →
        </button>
      </div>

      {/* CA Lane */}
      <div className="ca-lane" ref={caLaneRef}>
        <section id="cas1-section" className="ca-section">
          <CAS1
            onGateway={(accepted: boolean) => {
              if (accepted) navigateToCB("CBS1");
              else exitTo("SPLASH_1", "CAS1", "exit_1.1");
            }}
            onChatbotTrigger={(visible: boolean) =>
              handleChatbotTrigger("CAS1", visible)
            }
          />
        </section>
        <section id="cas2-section" className="ca-section">
          <CAS2
            isUnlocked={unlocked.CAS2}
            onCBS2Primary={() => navigateToCB("CBS2")}
            onCBS2Secondary={() => exitTo("SPLASH_3", "CAS2", "exit_3.1")}
            onChatbotTrigger={(visible: boolean) =>
              handleChatbotTrigger("CAS2", visible)
            }
          />
        </section>
        <section id="cas3-section" className="ca-section">
          <CAS3
            isUnlocked={unlocked.CAS3}
            onChatbotTrigger={(visible: boolean) =>
              handleChatbotTrigger("CAS3", visible)
            }
          />
        </section>
        <section id="cas4-section" className="ca-section">
          <CAS4
            isUnlocked={unlocked.CAS4}
            onChatbotTrigger={(visible: boolean) =>
              handleChatbotTrigger("CAS4", visible)
            }
          />
        </section>
      </div>

      {/* CB Lane */}
      <div className={`cb-lane ${currentLane === "CB" ? "active" : ""}`}>
        {currentCBSection === "CBS1" && (
          <div className="cb-section cb-section-scrollable">
            <button className="back-to-showcase" onClick={navigateToCA}>
              ← Back to Showcase
            </button>
            <div className="cb-content-wrapper">
              <CBS1
                onComplete={handleCBS1Complete}
                onDecline={() => exitTo("SPLASH_1", "CBS1", "exit_1.2")}
                onChatbotTrigger={() => handleCBS1ChatbotTrigger()}
              />
            </div>
          </div>
        )}
        {currentCBSection === "CBS2" && unlocked.CBS2 && (
          <div className="cb-section cb-section-scrollable">
            <button className="back-to-showcase" onClick={navigateToCA}>
              ← Back to Showcase
            </button>
            <div className="cb-content-wrapper">
              <CBS2
                onComplete={handleCBS2Complete}
                onDecline={() => exitTo("SPLASH_3", "CBS2", "exit_3.2")}
              />
            </div>
          </div>
        )}
        {currentCBSection === "CBS3" && unlocked.CBS3 && (
          <div className="cb-section cb-section-scrollable">
            <button className="back-to-showcase" onClick={navigateToCA}>
              ← Back to Showcase
            </button>
            <div className="cb-content-wrapper">
              <CBS3
                onComplete={(tier: string) => {
                  handleCBS3Complete({
                    amount: tier === "1000" ? 1000 : tier === "500" ? 500 : 0,
                    accredited: false,
                  });
                }}
                onDecline={() => exitTo("SPLASH_COMMITTED", "CBS3", "exit_4.3")}
              />
            </div>
          </div>
        )}
      </div>

      {/* CHATBOT */}
      <Chatbot
        isOpen={chatIsOpen}
        messages={chatMessages}
        quickReplies={chatReplies}
        showNotification={chatShowNotification}
        showPrompt={chatShowPrompt}
        promptText={PROMPT_TEXT[chatSection] || "Like what you see? 👀"}
        onOpen={handleChatOpen}
        onClose={handleChatClose}
        onPromptClick={handleChatPromptClick}
        onReplyClick={handleReplyClick}
      />

      {/* DEV BYPASS — admin only */}
      {profile?.is_admin && (
        <button
          onClick={() => {
            setUnlocked({
              CAS1: true,
              CAS2: true,
              CAS3: true,
              CAS4: true,
              CBS1: true,
              CBS2: true,
              CBS3: true,
            });
            advanceChatbotToSection("CAS3");
          }}
          style={{
            position: "fixed",
            top: 8,
            left: 8,
            zIndex: 99999,
            background: "#333",
            color: "#e8544f",
            border: "1px solid #e8544f",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: "0.7rem",
            cursor: "pointer",
            opacity: 0.6,
          }}
        >
          DEV: Unlock All
        </button>
      )}
    </div>
  );
}

export default App;
